<template>
  <div>
    <GmapMap
      ref="googleMap"
      :center="center"
      :zoom="15"
      :style="`width: 100%; height: ${div_height}px;`"
      :options="{
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: true,
        fullscreenControl: true,
        disableDefaultUI: true
      }"
      id="device-location__google-map"
    >
      <v-btn
        id="myLocation"
        small
        fab
        color="white"
        class="mr-3 mb-2"
        elevation="1"
        @click="getMyLocation(true)"
      >
        <v-icon>mdi-crosshairs-gps</v-icon>
      </v-btn>
      <GmapMarker
        v-for="(item, index) in marker"
        :key="'marker' + index"
        :position="item.coordinate"
        :clickable="true"
        :icon="require(`@/assets/icons/${markerColor(item.id)}.svg`)"
        @mouseover="showInfo(item)"
        @mouseout="show_info = false"
        :id="'device-location__marker' + index"
      >
      </GmapMarker>
      <GmapInfoWindow
        :opened="show_info"
        :position="info.coordinate"
        :options="{
          pixelOffset: {
            width: 0,
            height: -35
          }
        }"
        id="device-location__info"
      >
        <div class="font-weight-bold">{{ info.name }}</div>
        <div class="font-weight-bold">
          {{ $t("modules.home.altitude") }}: {{ info.altitude }}m
        </div>
        <div class="font-weight-bold">
          {{ $t("modules.home.lastUpdated") }}:
          {{ new Date(info.time).toLocaleString($i18n.locale) }}
        </div>
      </GmapInfoWindow>
    </GmapMap>
    <v-snackbar v-model="error" color="error" id="geolocation-error">
      {{ errorMsg }}
      <v-btn icon @click="error = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { gmapApi } from "vue2-google-maps";

export default {
  name: "DeviceLocation",
  data() {
    return {
      show_info: false,
      div_height: 0,
      info: {
        coordinate: this.center,
        name: "",
        altitude: 0,
        time: 0
      },
      error: false,
      errorMsg: "",
      center: {
        lat: 40.6967985,
        lng: -74.2291902
      }
    };
  },
  props: {
    deviceId: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: ""
    },
    details: {
      type: Array
    },
    devices: {
      type: Array
    }
  },

  computed: {
    google: gmapApi,
    marker() {
      return this.details.reduce((filtered, item) => {
        if (
          item.latest_location &&
          this.isCoordinateValid(item.latest_location.coordinate)
        ) {
          // get device name
          let device = this.devices.find(
            device => device.id === item.device_id
          );
          let deviceName, isActivated;
          if (device) {
            deviceName = device.name;
            isActivated = device.is_activated;
          } else {
            deviceName = "N/A";
            isActivated = false;
          }

          let data = {
            id: item.device_id,
            isActivated: isActivated,
            coordinate: {
              lat: item.latest_location.coordinate[1],
              lng: item.latest_location.coordinate[0]
            },
            name: deviceName,
            altitude: item.latest_location.altitude,
            time: item.latest_location.time
          };
          filtered.push(data);
        }
        return filtered;
      }, []);
    }
  },

  mounted() {
    this.getMyLocation(false);
    this.$refs.googleMap.$mapPromise.then(map => {
      this.div_height = 300;
      let myLocationDiv = document.createElement("div");
      let myLocationBtn = document.getElementById("myLocation");
      myLocationDiv.appendChild(myLocationBtn);

      myLocationDiv.index = 1;
      map.controls[this.google.maps.ControlPosition.RIGHT_BOTTOM].push(
        myLocationDiv
      );
    });
  },

  watch: {
    details: {
      deep: true,
      handler(value) {
        let data = value.find(item => item.device_id === this.deviceId);
        if (
          data &&
          data.latest_location &&
          this.isCoordinateValid(data.latest_location.coordinate)
        ) {
          this.center = {
            lat: data.latest_location.coordinate[1],
            lng: data.latest_location.coordinate[0]
          };
        } else {
          this.getMyLocation();
        }
      }
    },

    deviceId: {
      handler(value) {
        let data = this.details.find(item => item.device_id === value);
        if (
          data &&
          data.latest_location &&
          this.isCoordinateValid(data.latest_location.coordinate)
        ) {
          this.center = {
            lat: data.latest_location.coordinate[1],
            lng: data.latest_location.coordinate[0]
          };
        } else {
          this.getMyLocation();
        }
      }
    }
  },

  methods: {
    showInfo(item) {
      this.show_info = true;
      this.info.coordinate = item.coordinate;
      this.info.name = item.name;
      this.info.altitude = item.altitude;
      this.info.time = item.time;
    },

    markerColor(id) {
      let isOnline;
      let iconType;
      let getData = this.devices.find(item => item.id === id);
      if (getData) {
        isOnline = getData.availability === this.$t("modules.devices.online");
      } else {
        isOnline = false;
      }

      if (id === this.deviceId) {
        iconType = "selected-marker-";
      } else {
        iconType = "marker-";
      }

      // get location info
      let locationData = this.details.find(item => item.device_id === id);

      if (locationData.latest_location) {
        if (locationData.latest_location.accuracy > 0 && isOnline) {
          return iconType + "green";
        }
      }
      return iconType + "grey";
    },

    getMyLocation(notify) {
      //do we support geolocation
      if (!("geolocation" in navigator)) {
        this.error = notify;
        this.errorMsg = this.$t("error.geolocationUnsupported");
        return;
      }

      // get position
      navigator.geolocation.getCurrentPosition(
        pos => {
          // deviation : to make this.center watch for changes when pressed go to my location
          let deviation = Math.random() * 3;
          deviation = deviation / 100000;
          this.center = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude + deviation
          };
        },
        err => {
          this.error = notify;
          this.errorMsg = this.$t("error.geolocationDenied");
        }
      );
    },
    isCoordinateValid(coordinate) {
      return coordinate.reduce((total, item) => (total += item), 0) !== 0;
    }
  }
};
</script>
